import React, { useEffect, useMemo, useRef, useState } from "react";

import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";

import {
  FaCheck,
  FaCheckCircle,
  FaClipboardList,
  FaClock,
  FaCoins,
  FaEdit,
  FaEuroSign,
  FaFileAlt,
  FaMapMarkedAlt,
  FaPlus,
  FaPrint,
  FaRegTimesCircle,
  FaSave,
  FaTimes,
  FaTrash,
  FaUser,
  FaWallet,
} from "react-icons/fa";

import ExpenseStatusBadge from "./ExpenseStatusBadge";
import ExpenseClaimPrintSheet from "./ExpenseClaimPrintSheet";
import ExpenseMileageDetails from "./ExpenseMileageDetails";

import {
  fmtDate,
  fmtDateTime,
  getBudgetCodeLabel,
  getClaimantLabel,
  getEventDatePlace,
  getEventLabel,
  getEventMeta,
  isManualExpenseClaim,
  money,
  toAbsoluteFileUrl,
} from "../utils/expenseAdminFormat";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100";

const textareaClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100";

const EDITABLE_STATUSES = ["draft", "submitted", "rejected"];

const printPageStyle = `
  @page {
    size: A4 portrait;
    margin: 10mm;
  }

  @media print {
    html,
    body {
      background: #ffffff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    a[href]::after {
      content: "";
    }
  }
`;

function InfoItem({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>

      <div className="mt-1 text-sm font-medium text-slate-900">
        {value || "—"}
      </div>
    </div>
  );
}

function EditField({ label, children, className = "" }) {
  return (
    <div className={`rounded-2xl bg-slate-50 px-4 py-3 ${className}`}>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>

      {children}
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-3">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
          {icon}
        </div>

        <div className="text-base font-black text-slate-900">{title}</div>
      </div>

      {children}
    </section>
  );
}

function toInputDate(value) {
  if (!value) return "";

  if (typeof value === "string" && value.length >= 10) {
    return value.slice(0, 10);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
}

function toNullableNumber(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number) ? number : null;
}

function makeTempId() {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function buildInitialForm(claim) {
  return {
    code_budgetaire_id:
      claim?.code_budgetaire_id ?? claim?.BudgetCode?.id ?? "",

    commentaire: claim?.commentaire || "",

    trajet_necessaire: Boolean(claim?.trajet_necessaire),

    moyen_transport: claim?.moyen_transport || "",

    aller_retour: claim?.aller_retour ?? true,

    depart: claim?.depart || "",

    arrivee: claim?.arrivee || "",

    distance_km:
      claim?.distance_km === null || claim?.distance_km === undefined
        ? ""
        : claim.distance_km,

    taux_km:
      claim?.taux_km === null || claim?.taux_km === undefined
        ? ""
        : claim.taux_km,

    montant_trajet:
      claim?.montant_trajet === null || claim?.montant_trajet === undefined
        ? ""
        : claim.montant_trajet,

    covoiturage: Boolean(claim?.covoiturage),

    covoiturage_role: claim?.covoiturage_role || "",

    covoiturage_avec: claim?.covoiturage_avec || "",

    covoiturage_nb_passagers:
      claim?.covoiturage_nb_passagers === null ||
      claim?.covoiturage_nb_passagers === undefined
        ? ""
        : claim.covoiturage_nb_passagers,

    indemnite_tenue:
      claim?.indemnite_tenue === null || claim?.indemnite_tenue === undefined
        ? ""
        : claim.indemnite_tenue,

    indemnite_representation:
      claim?.indemnite_representation === null ||
      claim?.indemnite_representation === undefined
        ? ""
        : claim.indemnite_representation,
  };
}

function buildItemDraft(item) {
  return {
    id: item?.id || null,

    tempId: item?.id ? `item-${item.id}` : makeTempId(),

    type: item?.type || "",

    date_frais: toInputDate(item?.date_frais),

    description: item?.description || "",

    montant:
      item?.montant === null || item?.montant === undefined ? "" : item.montant,
  };
}

export default function ExpenseClaimDetailsDrawer({
  open,
  claim,
  loading,
  onClose,
  onApprove,
  onReject,
  onMarkPaid,
  onDelete,
  onSaveClaim,
  onCreateItem,
  onUpdateItem,
  onDeleteItem,
  actionLoading,
  eventDetailsMap = {},
  budgetCodes = [],
}) {
  const [adminComment, setAdminComment] = useState("");

  const [isEditing, setIsEditing] = useState(false);

  const [form, setForm] = useState(buildInitialForm(null));

  const [itemDrafts, setItemDrafts] = useState([]);

  const [itemBusyKey, setItemBusyKey] = useState("");

  const printRef = useRef(null);

  const runPrint = useReactToPrint({
    contentRef: printRef,

    documentTitle: claim?.id ? `fiche_frais_${claim.id}` : "fiche_frais",

    pageStyle: printPageStyle,
  });

  const editableStatus = EDITABLE_STATUSES.includes(claim?.statut);

  const manualEvent = isManualExpenseClaim(claim);

  useEffect(() => {
    setAdminComment(claim?.admin_commentaire || "");

    setForm(buildInitialForm(claim));

    setItemDrafts((claim?.ClaimItems || []).map(buildItemDraft));

    setIsEditing(false);
    setItemBusyKey("");
  }, [claim?.id, claim?.admin_commentaire, claim]);

  const itemAttachmentsMap = useMemo(() => {
    const map = new Map();

    (claim?.ClaimAttachments || []).forEach((attachment) => {
      if (!attachment.expense_claim_item_id) {
        return;
      }

      const key = attachment.expense_claim_item_id;

      if (!map.has(key)) {
        map.set(key, []);
      }

      map.get(key).push(attachment);
    });

    return map;
  }, [claim]);

  if (!open) return null;

  const setField = (field, value) => {
    setForm((previousForm) => ({
      ...previousForm,
      [field]: value,
    }));
  };

  const handlePrint = () => {
    if (!claim || loading) return;

    if (isEditing) {
      toast.info(
        "Enregistre ou annule les modifications avant d’imprimer la fiche.",
      );

      return;
    }

    if (!printRef.current) {
      toast.error("La version imprimable n’est pas encore prête.");

      return;
    }

    runPrint();
  };

  const handleCancelEdit = () => {
    setForm(buildInitialForm(claim));

    setItemDrafts((claim?.ClaimItems || []).map(buildItemDraft));

    setIsEditing(false);
    setItemBusyKey("");
  };

  const handleSaveClaim = async () => {
    if (!claim?.id || !onSaveClaim) return;

    if (form.covoiturage && form.moyen_transport !== "voiture") {
      toast.error(
        "Le covoiturage n'est possible qu'avec le moyen de transport 'voiture'.",
      );

      return;
    }

    if (form.covoiturage) {
      if (!["Conducteur", "Passager"].includes(form.covoiturage_role)) {
        toast.error(
          "Le rôle covoiturage doit être 'Conducteur' ou 'Passager'.",
        );

        return;
      }

      if (!String(form.covoiturage_avec || "").trim()) {
        toast.error("Le champ 'covoiturage avec' est obligatoire.");

        return;
      }
    }

    const payload = {
      /* Conservation du calcul multi-étapes existant */

      ...(claim?.mileage_detail?.result
        ? {
            mileage_mode: "detailed",
            mileage_detail: {
              roundTrip: Boolean(claim.mileage_detail.roundTrip),
              outbound: claim.mileage_detail.outbound,
              inbound: claim.mileage_detail.inbound,
            },
          }
        : {
            mileage_mode: "legacy",
          }),
      /*
       * Conservation explicite du contexte événement.
       * Pour une saisie manuelle, le controller gardera
       * l'événement technique et les champs saisis.
       */
      event_id: claim.event_id,

      event_mode: claim.event_mode || "calendar",

      titre_event: claim.titre_event || null,

      start_event: claim.start_event || null,

      lieu_event: claim.lieu_event || null,

      code_budgetaire_id: form.code_budgetaire_id
        ? Number(form.code_budgetaire_id)
        : null,

      commentaire: form.commentaire?.trim() || null,

      trajet_necessaire: Boolean(form.trajet_necessaire),

      moyen_transport: form.moyen_transport?.trim() || null,

      aller_retour: Boolean(form.aller_retour),

      depart: form.depart?.trim() || null,

      arrivee: form.arrivee?.trim() || null,

      distance_km: toNullableNumber(form.distance_km),

      taux_km: toNullableNumber(form.taux_km),

      montant_trajet: toNullableNumber(form.montant_trajet),

      covoiturage: Boolean(form.covoiturage),

      covoiturage_role:
        form.covoiturage && form.moyen_transport === "voiture"
          ? form.covoiturage_role || null
          : null,

      covoiturage_avec:
        form.covoiturage && form.moyen_transport === "voiture"
          ? form.covoiturage_avec?.trim() || null
          : null,

      covoiturage_nb_passagers:
        form.covoiturage &&
        form.moyen_transport === "voiture" &&
        form.covoiturage_role === "Conducteur"
          ? Number(form.covoiturage_nb_passagers || 0)
          : 0,

      indemnite_tenue: toNullableNumber(form.indemnite_tenue) ?? 0,

      indemnite_representation:
        toNullableNumber(form.indemnite_representation) ?? 0,
    };

    try {
      await onSaveClaim(payload);
      setIsEditing(false);
    } catch (error) {
      console.error("Erreur save claim :", error?.response?.data || error);
    }
  };

  const updateItemDraftField = (tempId, field, value) => {
    setItemDrafts((previousItems) =>
      previousItems.map((item) =>
        item.tempId === tempId
          ? {
              ...item,
              [field]: value,
            }
          : item,
      ),
    );
  };

  const handleAddItemDraft = () => {
    setItemDrafts((previousItems) => [
      ...previousItems,
      {
        id: null,
        tempId: makeTempId(),
        type: "",
        date_frais: "",
        description: "",
        montant: "",
      },
    ]);
  };

  const handleSaveItem = async (item) => {
    if (!item) return;

    if (!item.type?.trim()) {
      toast.error("Le type de ligne est obligatoire.");

      return;
    }

    if (!item.date_frais) {
      toast.error("La date de frais est obligatoire.");

      return;
    }

    if (
      item.montant === "" ||
      item.montant === null ||
      item.montant === undefined
    ) {
      toast.error("Le montant est obligatoire.");

      return;
    }

    const payload = {
      type: item.type.trim(),

      date_frais: item.date_frais,

      description: item.description?.trim() || null,

      montant: Number(item.montant || 0),
    };

    try {
      setItemBusyKey(item.tempId);

      if (item.id) {
        await onUpdateItem?.(item.id, payload);
      } else {
        await onCreateItem?.(payload);
      }
    } finally {
      setItemBusyKey("");
    }
  };

  const handleDeleteOneItem = async (item) => {
    if (!item) return;

    if (!item.id) {
      setItemDrafts((previousItems) =>
        previousItems.filter((row) => row.tempId !== item.tempId),
      );

      return;
    }

    const confirmed = window.confirm(
      `Supprimer cette ligne de frais ?${
        itemAttachmentsMap.get(item.id)?.length
          ? "\n\nLes justificatifs liés à cette ligne doivent également être supprimés côté back."
          : ""
      }`,
    );

    if (!confirmed) return;

    try {
      setItemBusyKey(item.tempId);

      await onDeleteItem?.(item.id);
    } finally {
      setItemBusyKey("");
    }
  };

  return (
    <div className="fixed inset-0 z-[90]">
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
        onClick={onClose}
      />

      <aside className="absolute right-0 top-0 h-full w-full max-w-3xl overflow-y-auto bg-slate-100 shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Fiche de frais
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-3">
                <h2 className="text-2xl font-black text-slate-900">
                  {claim ? `#${claim.id}` : "Chargement..."}
                </h2>

                {claim ? <ExpenseStatusBadge status={claim.statut} /> : null}

                {manualEvent ? (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200">
                    Hors calendrier
                  </span>
                ) : null}

                {editableStatus && !loading && claim ? (
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200">
                    Modifiable par l’administrateur
                  </span>
                ) : null}
              </div>

              {claim ? (
                <div className="mt-2 text-sm text-slate-500">
                  {getClaimantLabel(claim)} ·{" "}
                  {getEventLabel(claim, eventDetailsMap)}
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              {claim && !loading ? (
                <button
                  type="button"
                  onClick={handlePrint}
                  disabled={!claim || loading}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaPrint />
                  Imprimer / PDF
                </button>
              ) : null}

              {editableStatus && !loading && claim ? (
                <>
                  {!isEditing ? (
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <FaEdit />
                      Modifier
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        disabled={actionLoading === "save-claim"}
                        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FaTimes />
                        Annuler
                      </button>

                      <button
                        type="button"
                        onClick={handleSaveClaim}
                        disabled={actionLoading === "save-claim"}
                        className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FaSave />

                        {actionLoading === "save-claim"
                          ? "Enregistrement..."
                          : "Enregistrer"}
                      </button>
                    </>
                  )}
                </>
              ) : null}

              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-5 p-5">
          {loading || !claim ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
              Chargement du détail…
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 text-slate-500">
                    <FaEuroSign />

                    <span className="text-sm font-medium">Total général</span>
                  </div>

                  <div className="mt-3 text-2xl font-black text-slate-900">
                    {money(claim.total_general)}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 text-slate-500">
                    <FaClipboardList />

                    <span className="text-sm font-medium">Autres frais</span>
                  </div>

                  <div className="mt-3 text-2xl font-black text-slate-900">
                    {money(claim.total_frais)}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 text-slate-500">
                    <FaMapMarkedAlt />

                    <span className="text-sm font-medium">Trajet</span>
                  </div>

                  <div className="mt-3 text-2xl font-black text-slate-900">
                    {money(claim.montant_trajet)}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 text-slate-500">
                    <FaCoins />

                    <span className="text-sm font-medium">Indemnités</span>
                  </div>

                  <div className="mt-3 text-2xl font-black text-slate-900">
                    {money(
                      Number(claim.indemnite_tenue || 0) +
                        Number(claim.indemnite_representation || 0),
                    )}
                  </div>
                </div>
              </div>

              <Section icon={<FaUser />} title="Demandeur">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <InfoItem
                    label="Nom / prénom"
                    value={getClaimantLabel(claim)}
                  />

                  <InfoItem
                    label="Email"
                    value={claim?.Claimant?.email || "—"}
                  />

                  <InfoItem
                    label="Validateur"
                    value={
                      claim?.ClaimValidator
                        ? `${claim.ClaimValidator.prenom || ""} ${
                            claim.ClaimValidator.nom || ""
                          }`.trim()
                        : "—"
                    }
                  />

                  {!isEditing ? (
                    <InfoItem
                      label="Code budgétaire"
                      value={getBudgetCodeLabel(claim)}
                    />
                  ) : (
                    <EditField label="Code budgétaire">
                      <select
                        className={inputClass}
                        value={form.code_budgetaire_id}
                        onChange={(event) =>
                          setField("code_budgetaire_id", event.target.value)
                        }
                      >
                        <option value="">Aucun code</option>

                        {budgetCodes.map((code) => (
                          <option key={code.id} value={code.id}>
                            {code.code} · {code.libelle}
                          </option>
                        ))}
                      </select>
                    </EditField>
                  )}
                </div>
              </Section>

              <Section icon={<FaClock />} title="Suivi de la fiche">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <InfoItem
                    label="Créée le"
                    value={fmtDateTime(claim.created_at)}
                  />

                  <InfoItem
                    label="Dernière mise à jour"
                    value={fmtDateTime(claim.updated_at)}
                  />

                  <InfoItem
                    label="Date de soumission"
                    value={fmtDateTime(claim.date_soumission)}
                  />

                  <InfoItem
                    label={manualEvent ? "Réunion" : "Événement"}
                    value={getEventLabel(claim, eventDetailsMap)}
                  />

                  <InfoItem
                    label="Origine"
                    value={
                      manualEvent
                        ? "Saisie manuelle · Hors calendrier"
                        : getEventMeta(claim, eventDetailsMap) || "—"
                    }
                  />

                  <InfoItem
                    label="Date / lieu"
                    value={getEventDatePlace(claim, eventDetailsMap) || "—"}
                  />
                </div>
              </Section>

              <Section icon={<FaMapMarkedAlt />} title="Trajet et transport">
                {!isEditing ? (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <InfoItem
                      label="Trajet nécessaire"
                      value={claim.trajet_necessaire ? "Oui" : "Non"}
                    />

                    <InfoItem
                      label="Moyen de transport"
                      value={claim.moyen_transport || "—"}
                    />

                    <InfoItem
                      label="Aller / retour"
                      value={claim.aller_retour ? "Oui" : "Non"}
                    />

                    <InfoItem label="Départ" value={claim.depart || "—"} />

                    <InfoItem label="Arrivée" value={claim.arrivee || "—"} />

                    <InfoItem
                      label="Distance"
                      value={
                        claim.distance_km != null
                          ? `${claim.distance_km} km`
                          : "—"
                      }
                    />

                    <InfoItem
                      label="Taux kilométrique"
                      value={
                        claim.taux_km != null ? `${claim.taux_km} €/km` : "—"
                      }
                    />

                    <InfoItem
                      label="Montant trajet"
                      value={money(claim.montant_trajet)}
                    />

                    <InfoItem
                      label="Covoiturage"
                      value={claim.covoiturage ? "Oui" : "Non"}
                    />

                    {claim.covoiturage ? (
                      <>
                        <InfoItem
                          label="Rôle covoiturage"
                          value={claim.covoiturage_role || "—"}
                        />

                        <InfoItem
                          label="Avec"
                          value={claim.covoiturage_avec || "—"}
                        />

                        <InfoItem
                          label="Nb passagers"
                          value={String(claim.covoiturage_nb_passagers || 0)}
                        />
                      </>
                    ) : null}
                  </div>
                ) : (
                  <fieldset
                    disabled={!!claim?.mileage_detail?.result}
                    className="min-w-0"
                  >
                    {claim?.mileage_detail?.result && (
                      <div className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-200">
                        Le trajet de cette fiche a été calculé automatiquement
                        par étapes. Ses paramètres kilométriques sont protégés
                        pour conserver la cohérence des indemnités. Vous pouvez
                        modifier les autres informations de la fiche.
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                      <EditField label="Trajet nécessaire">
                        <select
                          className={inputClass}
                          value={form.trajet_necessaire ? "1" : "0"}
                          onChange={(event) =>
                            setField(
                              "trajet_necessaire",
                              event.target.value === "1",
                            )
                          }
                        >
                          <option value="0">Non</option>

                          <option value="1">Oui</option>
                        </select>
                      </EditField>

                      <EditField label="Moyen de transport">
                        <select
                          className={inputClass}
                          value={form.moyen_transport}
                          onChange={(event) => {
                            const value = event.target.value;

                            setForm((previousForm) => ({
                              ...previousForm,

                              moyen_transport: value,

                              ...(value !== "voiture"
                                ? {
                                    covoiturage: false,

                                    covoiturage_role: "",

                                    covoiturage_avec: "",

                                    covoiturage_nb_passagers: "",
                                  }
                                : {}),
                            }));
                          }}
                        >
                          <option value="">Sélectionner</option>

                          <option value="voiture">Voiture</option>

                          <option value="train">Train</option>

                          <option value="transport">Transport</option>

                          <option value="autre">Autre</option>
                        </select>
                      </EditField>

                      <EditField label="Aller / retour">
                        <select
                          className={inputClass}
                          value={form.aller_retour ? "1" : "0"}
                          onChange={(event) =>
                            setField("aller_retour", event.target.value === "1")
                          }
                        >
                          <option value="1">Oui</option>

                          <option value="0">Non</option>
                        </select>
                      </EditField>

                      <EditField label="Départ">
                        <input
                          type="text"
                          className={inputClass}
                          value={form.depart}
                          onChange={(event) =>
                            setField("depart", event.target.value)
                          }
                        />
                      </EditField>

                      <EditField label="Arrivée">
                        <input
                          type="text"
                          className={inputClass}
                          value={form.arrivee}
                          onChange={(event) =>
                            setField("arrivee", event.target.value)
                          }
                        />
                      </EditField>

                      <EditField label="Distance (km)">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          className={inputClass}
                          value={form.distance_km}
                          onChange={(event) =>
                            setField("distance_km", event.target.value)
                          }
                        />
                      </EditField>

                      <EditField label="Taux kilométrique (€/km)">
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          className={inputClass}
                          value={form.taux_km}
                          onChange={(event) =>
                            setField("taux_km", event.target.value)
                          }
                        />
                      </EditField>

                      <EditField label="Montant trajet">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className={inputClass}
                          value={form.montant_trajet}
                          onChange={(event) =>
                            setField("montant_trajet", event.target.value)
                          }
                        />
                      </EditField>

                      <EditField label="Covoiturage">
                        <select
                          className={inputClass}
                          value={form.covoiturage ? "1" : "0"}
                          onChange={(event) =>
                            setField("covoiturage", event.target.value === "1")
                          }
                        >
                          <option value="0">Non</option>

                          <option value="1">Oui</option>
                        </select>
                      </EditField>

                      {form.moyen_transport === "voiture" &&
                      form.covoiturage ? (
                        <>
                          <EditField label="Rôle covoiturage">
                            <select
                              className={inputClass}
                              value={form.covoiturage_role}
                              onChange={(event) =>
                                setField("covoiturage_role", event.target.value)
                              }
                            >
                              <option value="">Sélectionner</option>

                              <option value="Conducteur">Conducteur</option>

                              <option value="Passager">Passager</option>
                            </select>
                          </EditField>

                          <EditField label="Avec">
                            <input
                              type="text"
                              className={inputClass}
                              value={form.covoiturage_avec}
                              onChange={(event) =>
                                setField("covoiturage_avec", event.target.value)
                              }
                            />
                          </EditField>

                          <EditField label="Nb passagers">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              className={inputClass}
                              value={form.covoiturage_nb_passagers}
                              onChange={(event) =>
                                setField(
                                  "covoiturage_nb_passagers",
                                  event.target.value,
                                )
                              }
                            />
                          </EditField>
                        </>
                      ) : null}
                    </div>
                  </fieldset>
                )}

                {/* ========================================================
                    DETAIL DES TRAJETS KILOMETRIQUES
                    Uniquement pour les nouvelles fiches multi-étapes
                   ======================================================== */}

                {!isEditing && claim?.mileage_detail?.result && (
                  <ExpenseMileageDetails
                    mileageDetail={claim.mileage_detail}
                    savedDistance={claim.distance_km}
                    savedAmount={claim.montant_trajet}
                  />
                )}
              </Section>

              <Section icon={<FaWallet />} title="Indemnités">
                {!isEditing ? (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <InfoItem
                      label="Indemnité tenue"
                      value={money(claim.indemnite_tenue)}
                    />

                    <InfoItem
                      label="Indemnité représentation"
                      value={money(claim.indemnite_representation)}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <EditField label="Indemnité tenue">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={inputClass}
                        value={form.indemnite_tenue}
                        onChange={(event) =>
                          setField("indemnite_tenue", event.target.value)
                        }
                      />
                    </EditField>

                    <EditField label="Indemnité représentation">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={inputClass}
                        value={form.indemnite_representation}
                        onChange={(event) =>
                          setField(
                            "indemnite_representation",
                            event.target.value,
                          )
                        }
                      />
                    </EditField>
                  </div>
                )}
              </Section>

              <Section icon={<FaFileAlt />} title="Lignes de frais">
                {!itemDrafts.length ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
                    Aucune ligne de frais.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {itemDrafts.map((item) => (
                      <div
                        key={item.tempId}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        {!isEditing ? (
                          <>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-bold text-slate-900">
                                  {item.type || "Ligne"}
                                </div>

                                <div className="mt-1 text-xs text-slate-500">
                                  {fmtDate(item.date_frais)}
                                </div>
                              </div>

                              <div className="text-base font-black text-slate-900">
                                {money(item.montant)}
                              </div>
                            </div>

                            {item.description ? (
                              <div className="mt-3 text-sm text-slate-600">
                                {item.description}
                              </div>
                            ) : null}

                            {item.id &&
                            itemAttachmentsMap.get(item.id)?.length ? (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {itemAttachmentsMap
                                  .get(item.id)
                                  .map((attachment) => (
                                    <a
                                      key={attachment.id}
                                      href={toAbsoluteFileUrl(
                                        attachment.file_url,
                                      )}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-sky-700 ring-1 ring-sky-200 transition hover:bg-sky-50"
                                    >
                                      {attachment.original_name ||
                                        `Justificatif #${attachment.id}`}
                                    </a>
                                  ))}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <EditField label="Type">
                                <input
                                  type="text"
                                  className={inputClass}
                                  value={item.type}
                                  onChange={(event) =>
                                    updateItemDraftField(
                                      item.tempId,
                                      "type",
                                      event.target.value,
                                    )
                                  }
                                />
                              </EditField>

                              <EditField label="Date du frais">
                                <input
                                  type="date"
                                  className={inputClass}
                                  value={item.date_frais}
                                  onChange={(event) =>
                                    updateItemDraftField(
                                      item.tempId,
                                      "date_frais",
                                      event.target.value,
                                    )
                                  }
                                />
                              </EditField>

                              <EditField label="Montant">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className={inputClass}
                                  value={item.montant}
                                  onChange={(event) =>
                                    updateItemDraftField(
                                      item.tempId,
                                      "montant",
                                      event.target.value,
                                    )
                                  }
                                />
                              </EditField>

                              <EditField
                                label="Description"
                                className="md:col-span-2"
                              >
                                <textarea
                                  rows={3}
                                  className={textareaClass}
                                  value={item.description}
                                  onChange={(event) =>
                                    updateItemDraftField(
                                      item.tempId,
                                      "description",
                                      event.target.value,
                                    )
                                  }
                                />
                              </EditField>
                            </div>

                            {item.id &&
                            itemAttachmentsMap.get(item.id)?.length ? (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {itemAttachmentsMap
                                  .get(item.id)
                                  .map((attachment) => (
                                    <a
                                      key={attachment.id}
                                      href={toAbsoluteFileUrl(
                                        attachment.file_url,
                                      )}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-sky-700 ring-1 ring-sky-200 transition hover:bg-sky-50"
                                    >
                                      {attachment.original_name ||
                                        `Justificatif #${attachment.id}`}
                                    </a>
                                  ))}
                              </div>
                            ) : null}

                            <div className="mt-4 flex flex-wrap gap-3">
                              <button
                                type="button"
                                onClick={() => handleSaveItem(item)}
                                disabled={!!itemBusyKey}
                                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <FaSave />

                                {itemBusyKey === item.tempId
                                  ? "Enregistrement..."
                                  : item.id
                                    ? "Enregistrer la ligne"
                                    : "Ajouter la ligne"}
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDeleteOneItem(item)}
                                disabled={!!itemBusyKey}
                                className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <FaTrash />

                                {item.id ? "Supprimer la ligne" : "Retirer"}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {isEditing ? (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleAddItemDraft}
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <FaPlus />
                      Ajouter une ligne de frais
                    </button>
                  </div>
                ) : null}
              </Section>

              <Section icon={<FaFileAlt />} title="Justificatifs généraux">
                {!claim?.ClaimAttachments?.filter(
                  (attachment) => !attachment.expense_claim_item_id,
                ).length ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
                    Aucun justificatif général.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {claim.ClaimAttachments.filter(
                      (attachment) => !attachment.expense_claim_item_id,
                    ).map((attachment) => (
                      <a
                        key={attachment.id}
                        href={toAbsoluteFileUrl(attachment.file_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center rounded-full bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 transition hover:bg-white"
                      >
                        {attachment.original_name ||
                          `Justificatif #${attachment.id}`}
                      </a>
                    ))}
                  </div>
                )}
              </Section>

              <Section
                icon={<FaClipboardList />}
                title="Commentaire du demandeur"
              >
                {!isEditing ? (
                  <div className="rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                    {claim.commentaire || "—"}
                  </div>
                ) : (
                  <textarea
                    rows={4}
                    value={form.commentaire}
                    onChange={(event) =>
                      setField("commentaire", event.target.value)
                    }
                    className={textareaClass}
                    placeholder="Commentaire de la fiche..."
                  />
                )}
              </Section>

              <Section
                icon={<FaCheckCircle />}
                title="Traitement administrateur"
              >
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Commentaire administrateur
                    </label>

                    <textarea
                      rows={4}
                      value={adminComment}
                      onChange={(event) => setAdminComment(event.target.value)}
                      className={textareaClass}
                      placeholder="Ajouter une note de validation, un motif de rejet, une information de paiement…"
                    />
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {editableStatus && !isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setIsEditing(true)}
                          className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          <FaEdit />
                          Modifier la fiche
                        </button>

                        <button
                          type="button"
                          disabled={actionLoading === "delete-claim"}
                          onClick={onDelete}
                          className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FaTrash />

                          {actionLoading === "delete-claim"
                            ? "Suppression..."
                            : "Supprimer la fiche"}
                        </button>
                      </>
                    ) : null}

                    {claim.statut === "submitted" && !isEditing ? (
                      <>
                        <button
                          type="button"
                          disabled={actionLoading === "approve"}
                          onClick={() => onApprove(adminComment)}
                          className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FaCheck />

                          {actionLoading === "approve"
                            ? "Validation..."
                            : "Approuver"}
                        </button>

                        <button
                          type="button"
                          disabled={actionLoading === "reject"}
                          onClick={() => onReject(adminComment)}
                          className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FaRegTimesCircle />

                          {actionLoading === "reject" ? "Rejet..." : "Rejeter"}
                        </button>
                      </>
                    ) : null}

                    {claim.statut === "approved" && !isEditing ? (
                      <button
                        type="button"
                        disabled={actionLoading === "paid"}
                        onClick={() => onMarkPaid(adminComment)}
                        className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FaWallet />

                        {actionLoading === "paid"
                          ? "Mise à jour..."
                          : "Marquer comme payée"}
                      </button>
                    ) : null}

                    {claim.statut === "rejected" && !isEditing ? (
                      <div className="rounded-2xl bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800 ring-1 ring-rose-200">
                        Cette fiche a été rejetée mais peut encore être corrigée
                        puis re-traitée.
                      </div>
                    ) : null}

                    {claim.statut === "paid" ? (
                      <div className="rounded-2xl bg-sky-50 px-4 py-3 text-sm font-medium text-sky-800 ring-1 ring-sky-200">
                        Cette fiche est marquée comme payée.
                      </div>
                    ) : null}

                    {isEditing ? (
                      <div className="rounded-2xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 ring-1 ring-amber-200">
                        Pense à enregistrer la fiche puis, si besoin, chacune
                        des lignes de frais modifiées.
                      </div>
                    ) : null}
                  </div>
                </div>
              </Section>
            </>
          )}
        </div>

        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-[99999px] top-0"
        >
          {claim ? (
            <ExpenseClaimPrintSheet
              ref={printRef}
              claim={claim}
              eventDetailsMap={eventDetailsMap}
            />
          ) : null}
        </div>
      </aside>
    </div>
  );
}
